import type { ReactNode } from 'react';
import { Box, Card, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';

export type Tone = 'default' | 'success' | 'warning' | 'error' | 'info';

export function KpiTile({
  label,
  value,
  sub,
  icon,
  tone = 'default',
  loading,
  meter,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  loading?: boolean;
  /** 0..100: draws a thin meter under the value (disk, memory). */
  meter?: number | null;
}) {
  const color = tone === 'default' ? 'text.primary' : `${tone}.main`;
  return (
    <Card sx={{ height: '100%' }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary', mb: 0.5 }}>
          {icon && <Box sx={{ display: 'flex', fontSize: 18 }}>{icon}</Box>}
          <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }} noWrap>
            {label}
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton width="60%" height={36} />
        ) : (
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.25, color, fontVariantNumeric: 'tabular-nums' }} component="div">
            {value}
          </Typography>
        )}
        {meter !== undefined && meter !== null && !loading && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, meter)}
            color={meter >= 93 ? 'error' : meter >= 85 ? 'warning' : 'primary'}
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
            aria-label={`${label} ${meter}%`}
          />
        )}
        {sub && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {loading ? <Skeleton width="40%" /> : sub}
          </Typography>
        )}
      </Box>
    </Card>
  );
}
